import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown, Lock, Plus, UserPlus, Users, X } from "lucide-react";
import type { RecordData } from "../core";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { useSession } from "../react/providers/SessionProvider";
import { uidLabel } from "./quickMetaHelpers";
import { nameSearchDomain, USER_SEARCH_FIELDS } from "./chatterUserNames";

// Followers popover (communication Enh 03 Slice 3) — the Followers chip's
// click surface, mirroring the frozen mockup and the communication.follower
// model exactly:
//   - not following → header "+ Follow" creates the row (defaults all-on)
//     and opens the preference panel right away
//   - following → green "✓ Following" pill toggles the panel; the four
//     checkboxes are the model's subscription flags (on_message / on_email /
//     on_activity / on_notification) with their field help texts
//   - engine rule surfaced as a footnote, not a checkbox: internal notes
//     never notify
//   - rows carry `source` provenance tags (manual / auto · owner / mention);
//     external followers (partner_type=external) are labeled "messages &
//     emails only" — the engine ignores their activity/notification flags
//   - footer search adds internal users as followers
//
// Pure view (FollowersPopoverView) + thin container split, matching the
// other Enh 03 surfaces. Wire rows ship user_id as a raw uuid, so the
// container batch-resolves display names via one generic res.user search.

export const FOLLOWER_FLAGS = [
    { key: "on_message", label: "Messages", help: "When a message is posted on this record." },
    { key: "on_email", label: "Emails", help: "When an email is sent or received on this record." },
    { key: "on_activity", label: "Activities", help: "When an activity is logged or completed." },
    { key: "on_notification", label: "Notifications", help: "System events — status change, assignment, approval." },
] as const;

export type FollowerFlagKey = (typeof FOLLOWER_FLAGS)[number]["key"];

export interface FollowerEntry {
    followerId: string;
    label: string;
    /** Secondary line, e.g. "Client contact — messages & emails only". */
    sub: string;
    /** Provenance chip text ("manual" / "auto · owner" / "mention" / "external"). */
    sourceTag: string;
    isExternal: boolean;
    isMe: boolean;
    flags: Record<FollowerFlagKey, boolean>;
}

const SOURCE_LABELS: Record<string, string> = {
    manual: "manual",
    auto_owner: "auto · owner",
    mention: "mention",
};

/** Map a communication.follower wire row to the popover's display shape. */
export function toFollowerEntry(row: RecordData, myUserId: string): FollowerEntry {
    const followerId = typeof row.record_uuid === "string" ? row.record_uuid : String(row.id ?? "");
    const isExternal = row.partner_type === "external";
    const rawUser = typeof row.user_id === "string" ? row.user_id : "";
    return {
        followerId,
        label: isExternal ? "External contact" : uidLabel(rawUser || row.user_id),
        sub: isExternal ? "Client contact — messages & emails only" : "",
        sourceTag: isExternal ? "external" : SOURCE_LABELS[String(row.source ?? "")] ?? "manual",
        isExternal,
        isMe: !isExternal && Boolean(rawUser) && rawUser === myUserId,
        flags: {
            on_message: row.on_message !== false,
            on_email: row.on_email !== false,
            on_activity: row.on_activity !== false,
            on_notification: row.on_notification !== false,
        },
    };
}

function initials(label: string): string {
    const parts = label.trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p.charAt(0).toUpperCase()).join("") || "?";
}

interface FollowersPopoverViewProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    trigger: ReactNode;
    entries: FollowerEntry[];
    loading: boolean;
    /** My follower entry — null when not following. */
    me: FollowerEntry | null;
    prefsOpen: boolean;
    onTogglePrefs: () => void;
    onFollow: () => void;
    onUnfollow: () => void;
    onRemove: (entry: FollowerEntry) => void;
    onFlagChange: (flag: FollowerFlagKey, value: boolean) => void;
    searchValue: string;
    onSearchChange: (value: string) => void;
    searchResults: Array<{ uuid: string; label: string }>;
    onAddFollower: (userUuid: string) => void;
}

export function FollowersPopoverView({
    open,
    onOpenChange,
    trigger,
    entries,
    loading,
    me,
    prefsOpen,
    onTogglePrefs,
    onFollow,
    onUnfollow,
    onRemove,
    onFlagChange,
    searchValue,
    onSearchChange,
    searchResults,
    onAddFollower,
}: FollowersPopoverViewProps): ReactNode {
    const others = entries.filter((e) => !e.isMe);
    return (
        <Popover.Root open={open} onOpenChange={onOpenChange}>
            <Popover.Trigger asChild>{trigger}</Popover.Trigger>
            <Popover.Portal>
                <Popover.Content className="followers-pop" sideOffset={6} align="start">
                    <header className="followers-pop__head">
                        <Users size={14} aria-hidden="true" />
                        <span className="followers-pop__title">Followers</span>
                        <span className="followers-pop__sub">· notified of record updates</span>
                        {!me ? (
                            <button type="button" className="followers-pop__follow" onClick={onFollow}>
                                <Plus size={12} aria-hidden="true" />
                                Follow
                            </button>
                        ) : null}
                    </header>

                    <div className="followers-pop__list">
                        {loading ? (
                            <p className="followers-pop__empty">Loading…</p>
                        ) : (
                            <>
                                {me ? (
                                    <>
                                        <div className="followers-pop__row">
                                            <span className="followers-pop__avatar">{initials(me.label)}</span>
                                            <span className="followers-pop__name">
                                                {me.label} <small>you</small>
                                            </span>
                                            <button
                                                type="button"
                                                className="followers-pop__me-pill"
                                                onClick={onTogglePrefs}
                                                aria-expanded={prefsOpen}
                                            >
                                                <Check size={11} aria-hidden="true" />
                                                Following
                                                <ChevronDown size={11} aria-hidden="true" />
                                            </button>
                                        </div>
                                        {prefsOpen ? (
                                            <div className="followers-pop__prefs">
                                                <h5>Notify me on</h5>
                                                {FOLLOWER_FLAGS.map((flag) => (
                                                    <label className="followers-pop__pref" key={flag.key}>
                                                        <input
                                                            type="checkbox"
                                                            checked={me.flags[flag.key]}
                                                            onChange={(e) => onFlagChange(flag.key, e.target.checked)}
                                                        />
                                                        <span>
                                                            <span className="followers-pop__pref-label">{flag.label}</span>
                                                            <span className="followers-pop__pref-help">{flag.help}</span>
                                                        </span>
                                                    </label>
                                                ))}
                                                <p className="followers-pop__engine-note">
                                                    <Lock size={11} aria-hidden="true" />
                                                    Internal notes never trigger notifications — engine rule, not a preference.
                                                </p>
                                                <button type="button" className="followers-pop__unfollow" onClick={onUnfollow}>
                                                    Unfollow this record
                                                </button>
                                            </div>
                                        ) : null}
                                    </>
                                ) : null}
                                {others.map((entry) => (
                                    <div className="followers-pop__row" key={entry.followerId}>
                                        <span className="followers-pop__avatar">{initials(entry.label)}</span>
                                        <span className="followers-pop__name">
                                            {entry.label}
                                            <span className="followers-pop__src">{entry.sourceTag}</span>
                                            {entry.sub ? <small>{entry.sub}</small> : null}
                                        </span>
                                        <button
                                            type="button"
                                            className="followers-pop__remove"
                                            onClick={() => onRemove(entry)}
                                            aria-label={`Remove follower ${entry.label}`}
                                            title="Remove follower"
                                        >
                                            <X size={13} />
                                        </button>
                                    </div>
                                ))}
                                {!me && others.length === 0 ? (
                                    <p className="followers-pop__empty">No followers yet.</p>
                                ) : null}
                            </>
                        )}
                    </div>

                    <footer className="followers-pop__foot">
                        <div className="followers-pop__add">
                            <UserPlus size={13} aria-hidden="true" />
                            <input
                                value={searchValue}
                                onChange={(e) => onSearchChange(e.target.value)}
                                placeholder="Add follower — search users…"
                                aria-label="Add follower"
                            />
                        </div>
                        {searchResults.length > 0 ? (
                            <div className="followers-pop__results">
                                {searchResults.map((u) => (
                                    <button
                                        type="button"
                                        key={u.uuid}
                                        className="followers-pop__result"
                                        onClick={() => onAddFollower(u.uuid)}
                                    >
                                        <span className="followers-pop__avatar">{initials(u.label)}</span>
                                        {u.label}
                                    </button>
                                ))}
                            </div>
                        ) : null}
                    </footer>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}

interface FollowersPopoverProps {
    modelKey: string;
    recordUuid: string;
    trigger: ReactNode;
    /** Fired after follow/unfollow/remove so the strip can refresh counts. */
    onChanged?: () => void;
}

export function FollowersPopover({ modelKey, recordUuid, trigger, onChanged }: FollowersPopoverProps): ReactNode {
    const runtime = useRuntime();
    const session = useSession();
    const [open, setOpen] = useState(false);
    const [entries, setEntries] = useState<FollowerEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [prefsOpen, setPrefsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [searchResults, setSearchResults] = useState<Array<{ uuid: string; label: string }>>([]);

    const me = entries.find((e) => e.isMe) ?? null;

    const reload = async () => {
        try {
            const rows = await runtime.services.chatter.listFollowers(modelKey, recordUuid);
            const mapped = rows.map((row) => toFollowerEntry(row, session.userId));

            // Resolve internal user display names — the wire ships raw uuids.
            const rawUsers = rows.map((r) => (typeof r.user_id === "string" ? r.user_id : null));
            const unresolved = [...new Set(rawUsers.filter((v): v is string => Boolean(v)))];
            if (unresolved.length) {
                try {
                    const res = await runtime.services.record.search("res.user", {
                        domain: [["record_uuid", "in", unresolved]],
                        limit: unresolved.length,
                    });
                    const names = new Map(
                        res.records.map((u) => [
                            String(u.record_uuid ?? u.id ?? ""),
                            typeof u.name === "string" && u.name ? u.name : uidLabel(u),
                        ]),
                    );
                    mapped.forEach((entry, i) => {
                        const raw = rawUsers[i];
                        const resolvedName = raw ? names.get(raw) : undefined;
                        if (resolvedName && !entry.isExternal) entry.label = resolvedName;
                    });
                } catch {
                    /* keep uuid-prefix labels */
                }
            }
            setEntries(mapped);
        } catch {
            setEntries([]);
        }
    };

    useEffect(() => {
        if (!open) {
            setPrefsOpen(false);
            setSearchValue("");
            setSearchResults([]);
            return;
        }
        let active = true;
        setLoading(true);
        void reload().finally(() => {
            if (active) setLoading(false);
        });
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, modelKey, recordUuid]);

    // Debounced user search for the add-follower footer.
    useEffect(() => {
        if (!open || searchValue.trim().length < 2) {
            setSearchResults([]);
            return;
        }
        const handle = setTimeout(() => {
            const existing = new Set(entries.map((e) => e.followerId));
            void runtime.services.record
                .search("res.user", { domain: nameSearchDomain(searchValue.trim(), USER_SEARCH_FIELDS) as never, limit: 8 })
                .then((res) => {
                    const out = res.records
                        .map((u) => ({
                            uuid: String(u.record_uuid ?? u.id ?? ""),
                            label: typeof u.name === "string" && u.name ? u.name : uidLabel(u),
                        }))
                        .filter((u) => u.uuid && !existing.has(u.uuid));
                    setSearchResults(out);
                })
                .catch(() => setSearchResults([]));
        }, 250);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, searchValue]);

    const follow = async (userUuid: string, openPrefs: boolean) => {
        try {
            // UI default (user-directed): following subscribes to Messages +
            // Emails only — Activities and Notifications are opt-in via the
            // preference panel. (The model's own defaults stay all-on; the
            // narrowing is explicit from this surface.)
            await runtime.services.chatter.follow(modelKey, recordUuid, {
                user_id: userUuid,
                on_message: true,
                on_email: true,
                on_activity: false,
                on_notification: false,
            });
            await reload();
            onChanged?.();
            if (openPrefs) setPrefsOpen(true);
        } catch {
            /* row unchanged */
        }
    };

    const remove = async (followerId: string) => {
        try {
            await runtime.services.chatter.unfollow(followerId);
            await reload();
            onChanged?.();
        } catch {
            /* row unchanged */
        }
    };

    const onFlagChange = (flag: FollowerFlagKey, value: boolean) => {
        if (!me) return;
        // Optimistic flip; PATCH persists. Reload only on failure.
        setEntries((prev) =>
            prev.map((e) => (e.isMe ? { ...e, flags: { ...e.flags, [flag]: value } } : e)),
        );
        runtime.services.chatter.updateFollower(me.followerId, { [flag]: value }).catch(() => void reload());
    };

    return (
        <FollowersPopoverView
            open={open}
            onOpenChange={setOpen}
            trigger={trigger}
            entries={entries}
            loading={loading}
            me={me}
            prefsOpen={prefsOpen}
            onTogglePrefs={() => setPrefsOpen((v) => !v)}
            onFollow={() => void follow(session.userId, true)}
            onUnfollow={() => {
                if (me) void remove(me.followerId);
                setPrefsOpen(false);
            }}
            onRemove={(entry) => void remove(entry.followerId)}
            onFlagChange={onFlagChange}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchResults={searchResults}
            onAddFollower={(uuid) => {
                setSearchValue("");
                setSearchResults([]);
                void follow(uuid, false);
            }}
        />
    );
}
